/**
 * Display helpers shared by the product card badge, the batches panel and the
 * Expiry dashboard. Pure and tiny: the card imports this eagerly, so nothing
 * heavy belongs here.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-11-21' -> '21 Nov 2026'. Anything else is returned as-is. */
export function formatExpiryDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return String(iso || '');
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return String(iso);
  return `${Number(m[3])} ${month} ${m[1]}`;
}

/** 'Expired 3 days ago' / 'Expires today' / '45 days left'. */
export function daysLeftLabel(daysLeft) {
  const n = Number(daysLeft);
  if (!Number.isFinite(n)) return '';
  if (n < 0) {
    const ago = Math.abs(n);
    return `Expired ${ago} day${ago === 1 ? '' : 's'} ago`;
  }
  if (n === 0) return 'Expires today';
  return `${n} day${n === 1 ? '' : 's'} left`;
}

/**
 * Colours per status, all from theme tokens so dark mode follows
 * (docs: theme-system, --app-* tokens; no hardcoded fills).
 */
export function expiryStatusColors(status) {
  if (status === 'expired') {
    return { ink: 'var(--app-critical-ink)', wash: 'var(--app-wash-critical)', border: 'var(--app-critical-ink)' };
  }
  if (status === 'soon') {
    return { ink: 'var(--app-warning-ink)', wash: 'var(--app-wash-warning)', border: 'var(--app-warning-ink)' };
  }
  return { ink: 'var(--app-ink-secondary)', wash: 'var(--app-surface-3)', border: 'var(--app-border-strong)' };
}

/**
 * The card badge for a product row.
 * @returns {null | { label, status, title }} null when the row is not tracked.
 */
export function expiryBadge(product) {
  if (!product || !product.expiryTracked) return null;
  const next = product.nextExpiry;
  const untracked = Number(product.expiryUntrackedQty) || 0;
  if (!next || !next.expiryDate) {
    return {
      label: 'No expiry recorded',
      status: 'none',
      title: untracked > 0
        ? `${untracked} in stock with no expiry recorded. Click to add a batch.`
        : 'No batch with stock left. Click to add a batch.'
    };
  }
  const remaining = Number(next.remaining) || 0;
  const status = next.status === 'expired' || next.status === 'soon' ? next.status : 'ok';
  const extra = untracked > 0 ? ` ${untracked} more in stock with no expiry recorded.` : '';
  return {
    label: `Exp ${formatExpiryDate(next.expiryDate)} · ${remaining} left`,
    status,
    title: `Soonest expiry with stock left (estimate: oldest stock assumed sold first).${extra} Click to manage batches.`
  };
}

/**
 * Row fields from a GET /api/expiry/batches entry, so the card can update
 * after the panel adds, writes off or restores a batch without a reload.
 */
export function rowExpiryFromEntry(entry) {
  const batches = (Array.isArray(entry?.batches) ? entry.batches : [])
    .filter((b) => (Number(b.remaining) || 0) > 0 && b.expiryDate)
    .sort((a, b) => String(a.expiryDate).localeCompare(String(b.expiryDate)));
  const first = batches[0];
  return {
    expiryTracked: typeof entry?.tracked === 'boolean' ? entry.tracked : true,
    nextExpiry: first
      ? { expiryDate: first.expiryDate, remaining: Number(first.remaining) || 0, status: first.status, daysLeft: first.daysLeft }
      : null,
    expiryUntrackedQty: Number(entry?.untrackedQty) || 0
  };
}

const STORE_DOMAIN_BY_NAME = {
  'phaser fpv': 'phaser-fpv.myshopify.com',
  ccc: 'central-coast-coffee.myshopify.com',
  'central coast coffee': 'central-coast-coffee.myshopify.com'
};

/**
 * Store domains a card's product is also listed in (otherStoresLocations).
 * A food line sold on both stores can arrive on either store's PO, so the
 * "Tracks expiry" toggle flags every store the card shows. Rows without a
 * storeUrl fall back to the known store names; unknown stores are skipped.
 */
export function linkedExpiryStores(otherStoresLocations, ownStore) {
  const out = new Set();
  (Array.isArray(otherStoresLocations) ? otherStoresLocations : []).forEach((row) => {
    const domain = row?.storeUrl || STORE_DOMAIN_BY_NAME[String(row?.store || '').trim().toLowerCase()];
    if (domain && domain !== ownStore) out.add(domain);
  });
  return [...out];
}
